import { normalize } from "common/lib/normalize";
import { countActiveContracts } from "common/lib/paymentOptions";
import { getSortObj } from "common/lib/tableDataOptions";
import { getMdas } from "common/services/mda.service";
import Permission from "Components/Common/Permission";
import TableContainer from "Components/Common/TableContainer";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from "reactstrap";

// @ts-ignore
const TextWrapper: React.FC<{ value: string }> = ({ value }) => {
  if (!value) return null;
  const matches = value.match(/.{1,40}/g);
  const wrappedText = matches ? matches.join("\n") : ""; // Handle null case
  return (
    <div style={{ whiteSpace: "pre-wrap", width: "220px" }}>{wrappedText}</div>
  );
};

const MdaModal = ({ isOpen, toggle, title, filter }: any) => {
  const [params] = useState({
    page: 1, // Starts from 1
    pageSize: 20, // Default page size
  });

  const [pagination, setPagination] = useState({
    pageIndex: params.page - 1, // Convert to zero-based index
    pageSize: params.pageSize,
  });

  const [sorted, setSorted] = useState(() => {
    const sortParam = "";
    return sortParam
      ? JSON.parse(sortParam)
      : [{ id: "createdAt", desc: true }];
  });

  const [searchValue] = useState("");
  const [filterValue, setFilterValue] = useState(() => {
    return {
      agency: filter?.agency || null,
      status: filter?.status || "all",
      region: filter?.region || null,
      mda: filter?.mda || null,
      positionType: filter?.positionType || null,
      paymentCategory: filter?.paymentCategory || null,
      thematicArea: filter?.thematicArea || null,
      sector: filter?.sector || null,
      dateRange: filter?.dateRange || null,
    };
  });

  useEffect(() => {
    setFilterValue(() => {
      return {
        agency: filter?.agency || null,
        status: filter?.status || "all",
        region: filter?.region || null,
        mda: filter?.mda || null,
        positionType: filter?.positionType || null,
        paymentCategory: filter?.paymentCategory || null,
        thematicArea: filter?.thematicArea || null,
        sector: filter?.sector || null,
        dateRange: filter?.dateRange || null,
      };
    });
  }, [filter]);

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [meta, setMeta] = useState<any>({});
  const onPageChange = useCallback(
    async ({
      page,
      sorted,
      searchValue,
      filterValue,
    }: {
      page: number;
      sorted: Array<{ id: string; desc: boolean }>;
      searchValue: string;
      filterValue?: any;
    }) => {
      const sortObj = getSortObj(sorted);

      let andConditions = [];

      if (filterValue?.agency?.value) {
        andConditions.push({
          payments: {
            agency: {
              id: {
                $eq: filterValue.agency.value,
              },
            },
          },
        });
      }
      if (filterValue?.region?.value) {
        andConditions.push({
          region: {
            id: {
              $eq: filterValue.region.value,
            },
          },
        });
      }
      if (filterValue?.mda?.value) {
        andConditions.push({
          id: {
            $eq: filterValue.mda.value,
          },
        });
      }
      if (filterValue?.paymentCategory?.value) {
        andConditions.push({
          payments: {
            paymentCategory: {
              id: {
                $eq: filterValue.paymentCategory.value,
              },
            },
          },
        });
      }
      if (filterValue?.thematicArea?.value) {
        andConditions.push({
          payments: {
            thematicArea: {
              id: {
                $eq: filterValue.thematicArea.value,
              },
            },
          },
        });
      }
      if (filterValue?.sector?.value) {
        andConditions.push({
          payments: {
            sector: {
              id: {
                $eq: filterValue.sector.value,
              },
            },
          },
        });
      }
      if (filterValue?.positionType?.value) {
        andConditions.push({
          payments: {
            positionType: {
              id: {
                $eq: filterValue.positionType.value,
              },
            },
          },
        });
      }
      if (filterValue?.dateRange?.fromDate && filterValue?.dateRange?.toDate) {
        const fromDate =
          filterValue.dateRange.fromDate instanceof Date
            ? filterValue.dateRange.fromDate
            : new Date(filterValue.dateRange.fromDate);

        const toDate =
          filterValue.dateRange.toDate instanceof Date
            ? filterValue.dateRange.toDate
            : new Date(filterValue.dateRange.toDate);
        andConditions.push({
          $or: [
            {
              payments: {
                startDate: {
                  $between: [
                    fromDate.toLocaleDateString("en-CA"),
                    toDate.toLocaleDateString("en-CA"),
                  ],
                },
              },
            },
            {
              payments: {
                endDate: {
                  $between: [
                    fromDate.toLocaleDateString("en-CA"),
                    toDate.toLocaleDateString("en-CA"),
                  ],
                },
              },
            },
          ],
        });
      }
      // if (filterValue?.status) {
      //   if (filterValue.status === "active")
      //     andConditions.push({
      //       payments: {
      //         startDate: {
      //           $lte: new Date().toISOString(),
      //         },
      //         endDate: {
      //           $gte: new Date().toISOString(),
      //         },
      //       },
      //     });
      //   else if (filterValue.status === "inactive")
      //     andConditions.push({
      //       payments: {
      //         $or: [
      //           {
      //             startDate: {
      //               $gt: new Date().toISOString(),
      //             },
      //           },
      //           {
      //             endDate: {
      //               $lt: new Date().toISOString(),
      //             },
      //           },
      //         ],
      //       },
      //     });
      // }

      try {
        setLoading(true);

        const APIParams = {
          pagination: {
            page,
            pageSize: pagination.pageSize,
          },
          sort: sortObj,
          fields: ["name"],
          populate: {
            region: {
              fields: ["name"],
            },
            payments: {
              fields: ["startDate", "endDate"],
            },
          },
          filters: {
            payments: {
              id: {
                $notNull: true,
              },
            },
            $and: andConditions,
          },
        };
        const response: any = await getMdas(APIParams);
        setData(normalize(response.data));
        setMeta(response?.meta);
      } catch (e: any) {
        toast.error("Error while fetching data", {});
      } finally {
        setLoading(false);
      }
    },
    [pagination.pageSize]
  );

  const columns = useMemo(
    () => [
      {
        header: "Ministry/Department/Agency (MDA)",
        accessorKey: "name",
        id: "name",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const rowData = cell.row.original;

          return (
            <>
              <div
                className="d-flex align-items-center"
                style={{
                  width: "400px",
                }}
              >
                <div className="flex-shrink-0 me-3">
                  <div className="avatar-sm">
                    <div className="avatar-title border bg-primary-subtle text-danger rounded fs-24">
                      <i className="ri-bank-line text-primary"></i>
                    </div>
                  </div>
                </div>
                <div className="flex-grow-1">
                  <h5 className="fs-14 mb-1" style={{ whiteSpace: "pre-wrap" }}>
                    <Permission
                      alternative={
                        <Link to={"#"} className="text-body">
                          {cell.getValue()}
                        </Link>
                      }
                      action="findOne"
                      resource="mda"
                    >
                      <Link to={`/mdas/${rowData.id}`} className="text-body">
                        {cell.getValue()}
                      </Link>
                    </Permission>
                  </h5>
                  <p className="text-muted mb-0">
                    <span className="fw-medium">
                      {cell.row.original?.region?.name || ""}
                    </span>
                  </p>
                </div>
              </div>
            </>
          );
        },
      },
      {
        header: "Region",
        accessorKey: "region.name",
        enableColumnFilter: false,
        id: "region.name",
        cell: (cell: any) => <TextWrapper value={cell.getValue() || ""} />,
      },
      {
        header: "Contracts/Payments",
        accessorKey: "payments",
        id: "payments",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const rowData = cell.row.original;
          const noOfContracts = rowData?.payments?.length || 0;
          const label = noOfContracts === 1 ? "Contract" : "Contracts";
          return (
            <>
              <h5 className="fs-14 my-1 fw-normal">{noOfContracts}</h5>
              <span className="text-muted">{label}</span>
            </>
          );
        },
      },

      {
        header: "Number of Partners",
        accessorKey: "partners",
        enableSorting: false,
        enableColumnFilter: false,
        id: "partners",
        cell: (cell: any) => {
          const countOfActiveContracts = countActiveContracts(
            cell.row.original.payments
          );

          return (
            <React.Fragment>
              {countOfActiveContracts === 0 ? (
                <span className="badge bg-warning-subtle text-warning fs-11">
                  No Active Contracts
                </span>
              ) : countOfActiveContracts === 1 ? (
                <span className="badge bg-success-subtle text-success fs-11">
                  {countOfActiveContracts} Active Contract
                </span>
              ) : countOfActiveContracts > 1 ? (
                <span className="badge bg-success-subtle text-success fs-11">
                  {countOfActiveContracts} Active Contracts
                </span>
              ) : (
                <span className="badge bg-warning-subtle text-warning fs-11">
                  No Active Contracts
                </span>
              )}
            </React.Fragment>
          );
        },
      },
    ],
    []
  );

  return (
    <React.Fragment>
      <Modal
        isOpen={isOpen}
        toggle={toggle}
        size="xl"
        style={{ maxWidth: "95%", width: "95%", margin: "20px auto" }}
      >
        <ModalHeader toggle={toggle}>{title}</ModalHeader>
        <ModalBody>
          <Card>
            <CardHeader>
              <Row className="align-items-center g-3">
                <Col md={6}>
                  <h5 className="card-title mb-1">
                    Ministries, Departments and Agencies
                  </h5>
                  <p className="mb-0 text-muted">
                    A list of Government Ministries, Departments and Agencies.
                  </p>
                </Col>
              </Row>
            </CardHeader>
            <CardBody>
              <TableContainer
                loading={loading}
                columns={columns}
                data={data || []}
                pagination={pagination}
                setPagination={setPagination}
                sorted={sorted}
                setSorted={setSorted}
                onPageChange={onPageChange}
                FilterSection={() => {}}
                totalRows={meta?.pagination?.total || 0}
                totalPages={meta?.pagination?.pageCount || 0}
                searchValue={searchValue}
                filterValue={filterValue}
                divClass="table-responsive table-card mb-4"
                tableClass="align-middle table-nowrap mb-0 table-striped"
                theadClass="table-light table-nowrap"
              />
            </CardBody>
          </Card>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default MdaModal;
